import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { selectCurrentStep, setCurrentStep, setNewProjectName, setCreateNewProject } from 'app/sharedSlice';
import TargetProjectStep from 'features/ci-cd-flow/steps/TargetProjectStep';
import ProductionDatabaseStep from 'features/ci-cd-flow/steps/ProductionDatabaseStep';
import DatabaseTablesStep from 'features/ci-cd-flow/steps/DatabaseTablesStep';
import SoleConfigEditorStep from 'features/ci-cd-flow/steps/SoleConfigEditorStep';
import CiCdProjectSummaryStep from 'features/ci-cd-flow/steps/CiCdProjectSummaryStep';
import { sendMetrics, MetricType } from 'utils/metrics';
import useCiCdFlowSteps from 'features/ci-cd-flow/steps/cicdFlowSteps';
import type { IStep } from 'utils/types';

export default function CiCdFlowView(): JSX.Element {
  const steps: IStep[] = useCiCdFlowSteps();
  const [skipGrants, setSkipGrants] = useState<boolean>(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const currentStep = useAppSelector(selectCurrentStep);

  const setStepAndSendMetric = (step: number): void => {
    sendMetrics(MetricType.create_dynamic_delivery, step.toString());
    dispatch(setCurrentStep(step));
  };

  useEffect(() => {
    if (searchParams.has('project_name')) {
      dispatch(setCurrentStep(0));
      dispatch(setCreateNewProject(true));
      dispatch(setNewProjectName(searchParams.get('project_name') as string));
    }
  }, []);

  const getComponent = (name: string, index: number): React.ReactNode => {
    switch (name) {
      case 'Project setup':
        return (
          <TargetProjectStep
            onBack={index === 0 ? () => navigate('/') : () => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
            skipGrants={skipGrants}
            onSkipGrantsChange={setSkipGrants}
          />
        );
      case 'Database selection':
        return (
          <ProductionDatabaseStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Dataset schema':
        return (
          <DatabaseTablesStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
            skipGrants={skipGrants}
          />
        );
      case 'Configuration editor':
        return (
          <SoleConfigEditorStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Summary':
        return (
          <CiCdProjectSummaryStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
    }
  };

  return (
    <>
      {steps.map((step, index) => (
        <Fragment key={`${step.name}-${index}`}>{currentStep === index && getComponent(step.name, index)}</Fragment>
      ))}
    </>
  );
}
